import { useApolloClient } from '@apollo/client';
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { BbplMonitor } from '@finley-technologies/finley-types';
import { DbGraphUtils } from '@finley-technologies/tape-builder';

import { BbplMonitorInstanceGQLType } from 'types/dashboard-core-gqlTypes.generated';

import {
  GetBbplMonitorDocumentDocumentGQLType,
  GetBbplMonitorDocumentQueryGQLType,
  GetBbplMonitorDocumentQueryVariablesGQLType,
  GetBbplMonitorInstancesDocumentGQLType,
  GetBbplMonitorInstancesQueryGQLType,
  GetBbplMonitorInstancesQueryVariablesGQLType,
  GetBbplMonitorStatusDocumentGQLType,
  GetBbplMonitorStatusQueryGQLType,
  GetBbplMonitorStatusQueryVariablesGQLType,
} from '../requests/bbplMonitor.gql.generated';
import { BBPLGraphState, BBPLMonitorParams } from '../types/bbplMonitorTypes';

const useBBPLMonitorData = (params: BBPLMonitorParams) => {
  const {
    reportingDate,
    fileVersion,
    organizationId,
    latestArtifactDocumentId,
  } = params;
  const client = useApolloClient();

  const getStatus = useCallback(async () => {
    const { data } = await client.query<
      GetBbplMonitorStatusQueryGQLType,
      GetBbplMonitorStatusQueryVariablesGQLType
    >({
      query: GetBbplMonitorStatusDocumentGQLType,
      variables: {
        organizationId,
      },
    });

    return data;
  }, [client, organizationId]);

  const getInstances = useCallback(async () => {
    const { data } = await client.query<
      GetBbplMonitorInstancesQueryGQLType,
      GetBbplMonitorInstancesQueryVariablesGQLType
    >({
      query: GetBbplMonitorInstancesDocumentGQLType,
      variables: {
        reportingDate,
        fileVersion,
        organizationId,
      },
    });

    return data;
  }, [client, fileVersion, organizationId, reportingDate]);

  const getDocument = useCallback(
    async (artifactDocumentId: string) => {
      const { data } = await client.query<
        GetBbplMonitorDocumentQueryGQLType,
        GetBbplMonitorDocumentQueryVariablesGQLType
      >({
        query: GetBbplMonitorDocumentDocumentGQLType,
        variables: {
          artifactDocumentId,
        },
      });

      return data;
    },
    [client],
  );

  // fetches graph state from database.
  const fetchGraphData = useCallback(async (): Promise<BBPLGraphState> => {
    const instanceData = await getInstances();
    const { bbplMonitorInstanceRecords } = instanceData;

    const {
      artifactDocumentId: currentArtifactDocumentId,
      artifactInstances,
    } = bbplMonitorInstanceRecords;

    const artifactDocumentId =
      currentArtifactDocumentId || latestArtifactDocumentId;

    const document = await getDocument(artifactDocumentId);

    const { artifacts, edges } = DbGraphUtils.deserializeForBBPLMonitor(
      document.bbplMonitorArtifactDocumentInfo.artifactsDocumentString,
    );

    const artifactIdToInstanceMap = new Map<string, BbplMonitor.Instance>();
    artifactInstances.forEach((gqlInstance: BbplMonitorInstanceGQLType) => {
      const parsedInstance = JSON.parse(gqlInstance.instanceRecordString);
      const instance: BbplMonitor.Instance = {
        id: gqlInstance.id,
        artifactId: parsedInstance.artifactId,
        status: parsedInstance.status,
        updated_at: new Date(parsedInstance.updated_at),
        created_at: new Date(parsedInstance.created_at),
        statusDetails: parsedInstance.statusDetails,
      };
      artifactIdToInstanceMap.set(instance.artifactId, instance);
    });

    const nodes = artifacts.map((artifact) => ({
      artifact,
      instance: artifactIdToInstanceMap.get(artifact.id) || null,
    }));

    return {
      currentArtifactDocumentId: artifactDocumentId,
      latestArtifactDocumentId,
      nodes,
      edges,
    };
  }, [getDocument, getInstances, latestArtifactDocumentId]);

  return {
    getStatus,
    getInstances,
    fetchGraphData,
  };
};

export type BBPLMonitorContext = {
  state: BBPLGraphState;
};

const bbplMonitorContext = createContext<BBPLMonitorContext | null>(null);

export const useGraph = () => {
  const context = useContext(bbplMonitorContext);
  finleyAssert(
    context,
    'useBbplMonitorContext must be used within a BbplMonitorContextProvider.',
  );

  return context;
};

export type DisplayNode = {
  hasInputs: boolean;
  hasOutputs: boolean;
  artifact: BbplMonitor.Artifact;
  instance: BbplMonitor.Instance | null;
};

export const useNode = (nodeId: string): DisplayNode | null => {
  const { state } = useGraph();
  const node = state.nodes.find(({ artifact }) => artifact.id === nodeId);
  if (!node) {
    return null;
  }

  const hasInputs = state.edges.some((edge) => edge.target === nodeId);
  const hasOutputs = state.edges.some((edge) => edge.source === nodeId);
  return {
    hasInputs,
    hasOutputs,
    artifact: node.artifact,
    instance: node.instance,
  };
};

export const GraphProvider = ({
  children,
  params,
}: {
  children: React.ReactNode;
  params: BBPLMonitorParams;
}) => {
  const [value, setValue] = useState<BBPLMonitorContext | null>(null);
  const { fetchGraphData } = useBBPLMonitorData(params);

  useEffect(() => {
    const fetchData = async () => {
      const newState = await fetchGraphData();
      setValue({ state: newState });
    };
    fetchData();
  }, [fetchGraphData]);

  return (
    <bbplMonitorContext.Provider value={value}>
      {value && children}
    </bbplMonitorContext.Provider>
  );
};
